import { InvoiceParamsFromPosDTO, PaymentDTO, PaymentMethodDTO, Res, SaleDTO, DocumentType, CustomerDetailsDTO, Permission, BuyerCustomerDTO } from 'core/api';
import { Result } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { CashdrawerFacade, CustomerFacade } from 'pos/facades';
import { blockTicketWhileClosing, makeTicketReadyToClose } from 'pos/modules/helpers/document-creation';
import * as SalesStore from 'pos/modules/main/SalesStore';
import { ExternalPaymentsService, WhenPrintService } from 'pos/services';
import { PrepaymentStatus, PaymentDTOFactory } from 'pos/modules/helpers/payments-processing';
import { Message } from 'core/ui-next/modal';

type Params = {
	closeAs: 'Invoice' | 'Refund',
	method: PaymentMethodDTO,
	getAmountToPay: () => number,
	getTipAmount: () => number,
	getPendingAmount: () => number
}

export function requireCustomer(method: PaymentMethodDTO, currentCustomer: CustomerDetailsDTO): boolean {
	if (!method.RequireCustomer)
		return false;

	if (currentCustomer.Id === 0)
		return true;

	// Si tengo un cliente puesto y no me exigen un tipo específico, me sirve
	if (method.RequiredCustomerType.Id === 0)
		return false;

	// Si me pides un tipo y no tengo ese tipo, me hace falta
	return currentCustomer.Type.Id !== method.RequiredCustomerType.Id;
}

async function createInvoiceParams(payment: PaymentDTO, customer: BuyerCustomerDTO): Promise<InvoiceParamsFromPosDTO> {

	const printOnRemote = await WhenPrintService.shouldPrintInvoice(DocumentType.BasicInvoice);

	const closeParams: InvoiceParamsFromPosDTO = {
		InvoiceType: DocumentType.BasicInvoice,
		PrintTicket: printOnRemote,
		Payments: [payment],
		RelatedInvoiceId: null,
		PrintGiftTicket: false,
		BuyerCustomer: customer
	};

	return closeParams;
}

export async function closeCurrentTicketFromDirectPayment({ closeAs, method, getAmountToPay, getTipAmount, getPendingAmount }: Params): Promise<void> {

	const result = await makeTicketReadyToClose({
		closeAs,
		getTicket: SalesStore.getTicket,
		setTableCompanions: SalesStore.setTableCompanions,
		setFriendlyName: SalesStore.setFriendlyName,
	});

	if (!result.ok)
		return;

	const currentCustomer = await CustomerFacade.getUpdatedDetails(SalesStore.getTicket().Customer);

	// Si hace falta cliente, antes de nada lo ponemos
	if (requireCustomer(method, currentCustomer) && getAmountToPay() > 0) {
		const result = method.RequiredCustomerType.Id ? await Command.execute('searchCustomerByType', method.RequiredCustomerType) : await Command.execute('searchCustomerByText');
		if (!result.ok)
			return;

		await SalesStore.setCustomer(result.value);
	}

	const externalPayments = closeAs === 'Refund' ? ExternalPaymentsService.forRefund('Refund', SalesStore.getTicket().GlobalId) : ExternalPaymentsService.forPay('Pay', SalesStore.getTicket().GlobalId);
	// Metemos todo en una función autoinvocada para poder unificar la gestión de errores
	// si falla algo y tener el undoAll en un único punto
	const closeResult = await (async (): Promise<Result<SaleDTO, { isPrintError: boolean }>> => {

		const payment = PaymentDTOFactory.create(method, getAmountToPay(), getTipAmount());

		const closeParams = await createInvoiceParams(payment, SalesStore.getTicket().Customer);

		const checkResult = await SalesStore.checkRequiredCustomer(closeParams.Payments);
		if (!checkResult.IsValid) {
			await Message.show(checkResult.ErrorMessage);
			return Result.error({ isPrintError: false });
		}

		const paymentResult = await externalPayments.register(payment);
		if (!paymentResult.ok)
			return Result.error({ isPrintError: false });

		const change = payment.IsRefundVoucher ? 0 : getAmountToPay() - getPendingAmount();
		const cashPaymentResult = await externalPayments.commitCashPayments(change);
		if (!cashPaymentResult.ok)
			return Result.error({ isPrintError: false });

		const closeResult = closeAs === 'Refund' ? await SalesStore.refundTicket(closeParams) : await SalesStore.closeTicket(closeParams);
		if (closeResult.ok)
			return closeResult;

		return Result.error({ isPrintError: closeResult.error.isPrintError() });
	})();

	if (!closeResult.ok && !closeResult.error.isPrintError)
		await externalPayments.undoAll();
}


async function doDirectPayment({ methodId, amount }: { methodId: number, amount: Nullable<number> }): Promise<Result> {

	return blockTicketWhileClosing(SalesStore.getTicket().Id, async () => {

		const isRefund = SalesStore.ticketCanBeClosedAsRefund();

		if (isRefund) {
			const confirm = await Message.confirm(Res.Msg.MSG_EMIT_REFUND());
			if (!confirm.ok)
				return Result.error();
		}

		const getAmountToPay = () => Math.abs(amount || SalesStore.getTicket().PendingAmount);
		const getTipAmount = () => 0;
		const getPendingAmount = () => Math.abs(SalesStore.getTicket().PendingAmount);

		const operation = await CashdrawerFacade.createPaymentOperation();
		const method = operation.Methods.find(x => x.Id === methodId);

		if (!method) {
			await Message.show(Res.Msg.MSG_CANNOT_EMIT_PAYMENT_WITH_SELECTED_METHOD());
			return Result.error();
		}

		if (isRefund && !method.IsValidForRefund) {
			await Message.show(Res.Msg.MSG_CANNOT_EMIT_REFUND_WITH_SELECTED_PAYMENT_METHOD(method.Name));
			return Result.error();
		}

		const canPay = await PrepaymentStatus.canPay({ Method: method!, AmountToPay: getPendingAmount(), PaidAmount: getAmountToPay(), TipAmount: getTipAmount() });
		if (!canPay)
			return Result.error();

		await closeCurrentTicketFromDirectPayment({
			closeAs: isRefund ? 'Refund' : 'Invoice',
			method: method!,
			getAmountToPay,
			getTipAmount,
			getPendingAmount
		});

		return Result.ok();
	});
}

declare global {
	interface CommandType {
		doDirectPayment: ({ methodId, amount }: { methodId: number, amount: Nullable<number> }) => Promise<Result>
	}
}

Command.register('doDirectPayment', Preconditions.ensure(Permission.Ticket_Close), doDirectPayment);